/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable */
import type { ColumnDef } from '@tanstack/react-table'
import { useNavigate, useParams } from '@tanstack/react-router'
import { generatePath } from '@/shared/libs/generate-path'
import { Plus, Trash2 } from 'lucide-react'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type SurveyVersionDTO,
  useDeleteSurveyVersionMutation,
  useSurveyVersionsQuery,
  useUpdateSurveyVersionMutation,
} from '@/entities/survey'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { useTenantListScope } from '@/shared/hooks/use-tenant-list-scope'
import { showToast } from '@/shared/libs/toast'
import { resolveDashboardLocalizedValue } from '@/shared/utils/dashboard-localized-value'
import { Button } from '@/shared/ui/button'
import { Card, CardContent } from '@/shared/ui/card'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { DataTable, DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Switch } from '@/shared/ui/switch'
import { formatDateTime } from '@/shared/api/utils/helpers'

const SURVEY_FILTER_VALUE = {
  ALL: 'all',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const

type SurveyStatusFilter = (typeof SURVEY_FILTER_VALUE)[keyof typeof SURVEY_FILTER_VALUE]

export function SurveyListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const params = useParams({ strict: false }) as { tenantId?: string }
  const routeTenantId = params.tenantId

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [statusFilter, setStatusFilter] = useState<SurveyStatusFilter>(SURVEY_FILTER_VALUE.ACTIVE)

  const {
    isSuperAdmin,
    setActiveTenantId,
    tenantOptions,
    currentTenantId,
    currentTenantName,
    selectedLanguageCode,
    setSelectedLanguageCode,
    languageOptions,
    defaultLanguageCode,
    isLanguagesReady,
    handleLanguageScroll,
  } = useTenantListScope({ routeTenantId })

  const updateVersionMutation = useUpdateSurveyVersionMutation()
  const deleteVersionMutation = useDeleteSurveyVersionMutation()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useLayoutEffect(() => {
    setPage(1)
    setStatusFilter(SURVEY_FILTER_VALUE.ACTIVE)
  }, [currentTenantId])

  /** Dùng ngôn ngữ effective (selection hoặc fallback default tenant) ngay từ render đầu để API fetch không phải chờ effect set state. */
  const effectiveLanguageCode = selectedLanguageCode || defaultLanguageCode

  const versionsQuery = useSurveyVersionsQuery(
    currentTenantId,
    {
      current: page,
      pageSize,
      ...(effectiveLanguageCode ? { languageCode: effectiveLanguageCode } : {}),
      ...(statusFilter === SURVEY_FILTER_VALUE.ACTIVE
        ? { isActive: true }
        : statusFilter === SURVEY_FILTER_VALUE.INACTIVE
          ? { isActive: false }
          : {}),
    },
    { enabled: isLanguagesReady },
  )

  const versions = versionsQuery.data?.data ?? []
  const total = versionsQuery.data?.total ?? 0
  const displayLanguage = effectiveLanguageCode
  /** Language luôn bám ngôn ngữ default của tenant nên không tính vào số filter "đang áp dụng". */
  const activeFilterCount = [
    selectedLanguageCode && selectedLanguageCode !== defaultLanguageCode
      ? selectedLanguageCode
      : '',
    statusFilter !== SURVEY_FILTER_VALUE.ALL ? statusFilter : '',
  ].filter(Boolean).length

  function resetAdvancedFilters() {
    setSelectedLanguageCode(defaultLanguageCode)
    setStatusFilter(SURVEY_FILTER_VALUE.ACTIVE)
    setPage(1)
  }

  /**
   * Switches the active tenant and resets the filters scoped to the previous tenant.
   */
  function handleTenantChange(tenantId: string) {
    setActiveTenantId(tenantId)
    setPage(1)
    setSelectedLanguageCode('')
  }

  const handleToggleActive = useCallback(
    async (versionId: string, next: boolean) => {
      try {
        await updateVersionMutation.mutateAsync({
          versionId,
          payload: { isActive: next },
        })
        showToast.success(t('common.saved', { defaultValue: 'Saved.' }))
      } catch {
        showToast.error(
          t('survey.saveVersionError', { defaultValue: 'Could not save survey version.' }),
        )
      }
    },
    [t, updateVersionMutation],
  )

  const columns = useMemo<ColumnDef<SurveyVersionDTO>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'id',
        header: t('survey.columnTitle'),
        cell: ({ row }) => (
          <span className="font-medium text-zinc-900">
            {resolveDashboardLocalizedValue(row.original.localizedData, displayLanguage)}
          </span>
        ),
      },
      {
        accessorKey: 'business',
        header: t('survey.columnBusiness'),
        cell: ({ row }) => (
          <span className="text-zinc-600">{row.original.business?.trim() || '—'}</span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: t('survey.columnUpdated'),
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <div
            className="flex justify-center"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <Switch
              checked={row.original.isActive}
              disabled={updateVersionMutation.isPending}
              onCheckedChange={(v) => void handleToggleActive(row.original.id, v)}
            />
          </div>
        ),
      },
      {
        id: 'actions',
        size: 64,
        header: () => null,
        cell: ({ row }) => (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteVersionMutation.isPending}
              onClick={() => setDeletingId(row.original.id)}
            >
              <Trash2 size={14} className="mr-1.5" />
              {t('common.delete')}
            </Button>
          </div>
        ),
      },
    ],

    [
      handleToggleActive,
      displayLanguage,
      t,
      updateVersionMutation.isPending,
      deleteVersionMutation.isPending,
    ],
  )

  return (
    <PageContainer>
      <PageHeader
        title={t('survey.listTitle')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(currentTenantName
            ? [
                {
                  label: currentTenantName,
                  path: scopedAdminPath(
                    generatePath('/tenant/:tenantId', { tenantId: currentTenantId }),
                  ),
                },
              ]
            : []),
          { label: t('survey.listTitle') },
        ]}
        actions={
          currentTenantId ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  void navigate({
                    href: scopedAdminPath(
                      generatePath('/tenant/:tenantId/surveys/create', {
                        tenantId: currentTenantId,
                      }),
                    ),
                  })
                }
              >
                <Plus className="mr-1.5 size-4" />
                {t('survey.createVersion', { defaultValue: 'New version' })}
              </Button>
            </div>
          ) : null
        }
      />

      <DataTableToolbar>
        {isSuperAdmin && (
          <DataTableFilterField label={t('sidebar.tenants')}>
            <Select value={currentTenantId || ''} onValueChange={handleTenantChange}>
              <SelectTrigger className="w-60">
                <SelectValue placeholder={t('sidebar.tenants')} />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>
        )}

        <FilterPopover
          title={t('common.filter')}
          resetLabel={t('common.reset')}
          activeCount={activeFilterCount}
          onReset={resetAdvancedFilters}
        >
          <DataTableFilterField label={t('common.language')}>
            <Select
              value={selectedLanguageCode || defaultLanguageCode}
              onValueChange={(value) => {
                setSelectedLanguageCode(value)
                setPage(1)
              }}
              disabled={!currentTenantId || languageOptions.length < 1}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.language')} />
              </SelectTrigger>
              <SelectContent onViewportScroll={handleLanguageScroll}>
                {languageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('common.status')}>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as SurveyStatusFilter)
                setPage(1)
              }}
              disabled={!currentTenantId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SURVEY_FILTER_VALUE.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                <SelectItem value={SURVEY_FILTER_VALUE.ACTIVE}>{t('common.active')}</SelectItem>
                <SelectItem value={SURVEY_FILTER_VALUE.INACTIVE}>{t('common.inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>
        </FilterPopover>
      </DataTableToolbar>

      {versionsQuery.isError && (
        <Card className="mt-4 border-red-200 bg-red-50/80">
          <CardContent className="pt-6 text-sm text-red-800">{t('survey.loadError')}</CardContent>
        </Card>
      )}

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={versions}
          loading={!isLanguagesReady || versionsQuery.isLoading}
          emptyText={t('survey.emptyVersions')}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage)
              setPageSize(nextPageSize)
            },
          }}
          onRowClick={(version) =>
            void navigate({
              href: scopedAdminPath(
                generatePath('/tenant/:tenantId/surveys/:versionId', {
                  tenantId: currentTenantId,
                  versionId: version.id,
                }),
              ),
            })
          }
        />
      </div>

      <ConfirmDialog
        open={Boolean(deletingId)}
        title={t('survey.deleteConfirm', { defaultValue: 'Delete survey version?' })}
        description={t('survey.deleteConfirmDesc', {
          defaultValue: 'This action cannot be undone.',
        })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        variant="destructive"
        loading={deleteVersionMutation.isPending}
        onCancel={() => setDeletingId(null)}
        onConfirm={async () => {
          if (!deletingId || deleteVersionMutation.isPending) return
          try {
            await deleteVersionMutation.mutateAsync(deletingId)
            showToast.success(
              t('survey.deleteSuccess', { defaultValue: 'Survey version deleted.' }),
            )
          } catch {
            showToast.error(
              t('survey.deleteError', { defaultValue: 'Could not delete survey version.' }),
            )
          } finally {
            setDeletingId(null)
          }
        }}
      />
    </PageContainer>
  )
}
